import {controller} from '@github/catalyst'
import jsQR from 'jsqr'

// Camera-based QR ticket scanner for the event manage area. Decodes ticket
// tokens from the camera feed (or a manually entered code) and POSTs them to
// `data-endpoint` along with the selected product, then shows the redemption
// result. Repeated reads of the same code are debounced so one ticket held up
// to the camera doesn't fire "already redeemed" right after a success.
@controller
class QrTicketScannerElement extends HTMLElement {
  private stream: MediaStream | null = null
  private scanTimer: number | null = null
  private resultTimer: number | null = null
  private canvas = document.createElement('canvas')
  private submitting = false
  private lastToken = ''
  private lastTokenAt = 0

  get endpoint(): string {
    return this.getAttribute('data-endpoint') || ''
  }

  get csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
  }

  connectedCallback() {
    this.query('[data-qr-start]')?.addEventListener('click', this.startCamera)
    this.query('[data-qr-stop]')?.addEventListener('click', this.stopCamera)
    this.query('[data-qr-manual-submit]')?.addEventListener('click', this.submitManual)
    this.query('[data-qr-manual-input]')?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') this.submitManual()
    })
  }

  disconnectedCallback() {
    this.stopCamera()
    if (this.resultTimer) {
      window.clearTimeout(this.resultTimer)
      this.resultTimer = null
    }
  }

  private query<T extends HTMLElement>(selector: string): T | null {
    return this.querySelector<T>(selector)
  }

  private startCamera = async () => {
    const video = this.query<HTMLVideoElement>('[data-qr-video]')
    if (!video) return

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {facingMode: {ideal: 'environment'}},
        audio: false,
      })
    } catch {
      this.renderResult('invalid', 'Camera unavailable. Allow camera access in your browser, or enter the code manually.')
      return
    }

    video.srcObject = this.stream
    await video.play()

    video.classList.remove('hidden')
    this.query('[data-qr-placeholder]')?.setAttribute('hidden', '')
    this.query('[data-qr-start]')?.setAttribute('hidden', '')
    this.query('[data-qr-stop]')?.removeAttribute('hidden')

    this.scanTimer = window.setInterval(this.scanFrame, 250)
  }

  private stopCamera = () => {
    if (this.scanTimer) {
      window.clearInterval(this.scanTimer)
      this.scanTimer = null
    }
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null

    const video = this.query<HTMLVideoElement>('[data-qr-video]')
    if (video) {
      video.srcObject = null
      video.classList.add('hidden')
    }
    this.query('[data-qr-placeholder]')?.removeAttribute('hidden')
    this.query('[data-qr-stop]')?.setAttribute('hidden', '')
    this.query('[data-qr-start]')?.removeAttribute('hidden')
  }

  private scanFrame = () => {
    if (this.submitting) return
    const video = this.query<HTMLVideoElement>('[data-qr-video]')
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return

    // Downscale before decoding: jsQR is O(pixels) and door scanning happens at
    // arm's length, so full camera resolution is wasted work.
    const scale = Math.min(1, 640 / video.videoWidth)
    const width = Math.floor(video.videoWidth * scale)
    const height = Math.floor(video.videoHeight * scale)
    if (width === 0 || height === 0) return

    this.canvas.width = width
    this.canvas.height = height
    const context = this.canvas.getContext('2d', {willReadFrequently: true})
    if (!context) return
    context.drawImage(video, 0, 0, width, height)
    const image = context.getImageData(0, 0, width, height)

    const code = jsQR(image.data, width, height, {inversionAttempts: 'dontInvert'})
    if (code?.data) this.handleToken(code.data.trim())
  }

  private submitManual = () => {
    const input = this.query<HTMLInputElement>('[data-qr-manual-input]')
    const token = input?.value.trim()
    if (!token) return
    this.lastToken = ''
    this.handleToken(token)
    if (input) input.value = ''
  }

  private async handleToken(token: string) {
    const now = Date.now()
    if (token === this.lastToken && now - this.lastTokenAt < 5000) return
    this.lastToken = token
    this.lastTokenAt = now

    const product = this.query<HTMLSelectElement>('[data-qr-product]')
    if (!product?.value) {
      this.renderResult('invalid', 'Select a product to scan tickets for.')
      return
    }

    this.submitting = true
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify({token, product_id: product.value}),
      })
      const result = await response.json()
      this.renderResult(result.status, result.message, result.holder)
    } catch {
      this.renderResult('invalid', 'Something went wrong checking the ticket. Try again.')
    } finally {
      this.submitting = false
    }
  }

  private renderResult(status: string, message: string, holder?: string) {
    const container = this.query('[data-qr-result]')
    if (!container) return

    const styles: Record<string, {box: string; icon: string; title: string}> = {
      redeemed: {
        box: 'bg-green-500/10 border-green-500/40 text-green-400',
        icon: '✓',
        title: 'Valid ticket',
      },
      already_redeemed: {
        box: 'bg-yellow-500/10 border-yellow-500/40 text-yellow-400',
        icon: '⚠',
        title: 'Already redeemed',
      },
      invalid: {
        box: 'bg-red-500/10 border-red-500/40 text-red-400',
        icon: '✕',
        title: 'Not valid',
      },
    }
    const style = styles[status] || styles.invalid

    container.innerHTML = ''
    const box = document.createElement('div')
    box.className = `flex items-start gap-3 rounded-xl border px-4 py-3 ${style.box}`

    const icon = document.createElement('span')
    icon.className = 'text-2xl leading-none mt-0.5'
    icon.textContent = style.icon

    const body = document.createElement('div')
    const title = document.createElement('p')
    title.className = 'font-semibold'
    title.textContent = style.title
    body.appendChild(title)

    const detail = document.createElement('p')
    detail.className = 'text-sm text-zinc-300'
    detail.textContent = holder ? `${message} · ${holder}` : message
    body.appendChild(detail)

    box.append(icon, body)
    container.appendChild(box)
    container.removeAttribute('hidden')

    if (this.resultTimer) window.clearTimeout(this.resultTimer)
    this.resultTimer = window.setTimeout(() => {
      container.setAttribute('hidden', '')
      container.innerHTML = ''
    }, 5000)
  }
}
